import './MonthlyPnLTable.scss';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@trackman/web-shared-components';
import Paper from '@trackman/web-shared-components/components/Paper/Paper';
import { NoData } from 'pages/Admin/Analytics/components/NoData/NoData';
import { SourceBadge } from 'pages/Admin/Analytics/components/SourceBadge/SourceBadge';
import { currencyFormatter, formatMonth, indexByMonth, mergeMonthKeys, parseMonthKey } from 'pages/Admin/Analytics/utils/monthlySeries';
import {
  useGetCertRevenueBreakdown,
  useGetMonthlyChurnRates,
  useGetMonthlyRefunds,
  useGetMonthlySubscriptionRevenue,
} from 'queryHooks/Admin/analytics';

export const MonthlyPnLTable = ({ months = 6 }: { months?: number }) => {
  const { rows, isLoading, isError } = useMonthlyPnLBreakdown(months);
  const hasData = Boolean(rows.length);

  return (
    <Paper className='analytics-card monthly-pnl-table'>
      <div className='analytics-card__title-group'>
        <span className='card-title'>Monthly P&L breakdown</span>
        <SourceBadge source='RECURLY' />
      </div>
      <p className='analytics-card__subtitle'>
        Subscription revenue, certification revenue, refunds, and churn by month.
      </p>

      {isLoading || isError || !hasData ? (
        <NoData loading={isLoading} error={isError} noData={!hasData} />
      ) : (
        <div className='monthly-pnl-table__content'>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Month</TableCell>
                <TableCell align='right'>Sub Revenue</TableCell>
                <TableCell align='right'>Cert Revenue</TableCell>
                <TableCell align='right'>Gross</TableCell>
                <TableCell align='right'>Refunds</TableCell>
                <TableCell align='right'>Net Revenue</TableCell>
                <TableCell align='right'>Churn %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.year}-${row.month}`}>
                  <TableCell>{formatMonth(row.year, row.month)}</TableCell>
                  <TableCell align='right'>{currencyFormatter.format(row.subRevenue)}</TableCell>
                  <TableCell align='right'>{currencyFormatter.format(row.certRevenue)}</TableCell>
                  <TableCell align='right'>{currencyFormatter.format(row.gross)}</TableCell>
                  <TableCell align='right' className='monthly-pnl-table__negative'>
                    -{currencyFormatter.format(row.refunds)}
                  </TableCell>
                  <TableCell align='right'>{currencyFormatter.format(row.netRevenue)}</TableCell>
                  <TableCell align='right'>{row.churnRate !== null ? `${row.churnRate.toFixed(2)}%` : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Paper>
  );
};

export default MonthlyPnLTable;

type MonthlyPnLRow = {
  year: number;
  month: number;
  subRevenue: number;
  certRevenue: number;
  gross: number;
  refunds: number;
  netRevenue: number;
  churnRate: number | null;
};

const useMonthlyPnLBreakdown = (months = 6) => {
  const { data: subRevenueData, isLoading: isSubRevenueLoading, isError: isSubRevenueError } = useGetMonthlySubscriptionRevenue(months);
  const { data: certRevenueData, isLoading: isCertRevenueLoading, isError: isCertRevenueError } = useGetCertRevenueBreakdown(months);
  const { data: refundsData, isLoading: isRefundsLoading, isError: isRefundsError } = useGetMonthlyRefunds(months);
  const { data: churnData, isLoading: isChurnLoading, isError: isChurnError } = useGetMonthlyChurnRates(months);

  const isLoading = isSubRevenueLoading || isCertRevenueLoading || isRefundsLoading || isChurnLoading;
  const isError = isSubRevenueError || isCertRevenueError || isRefundsError || isChurnError;

  const subRevenueByMonth = indexByMonth(subRevenueData);
  const certRevenueByMonth = indexByMonth(certRevenueData);
  const refundsByMonth = indexByMonth(refundsData);
  const churnByMonth = indexByMonth(churnData);

  const keys = mergeMonthKeys(subRevenueData, certRevenueData, refundsData, churnData);

  const rows: MonthlyPnLRow[] = keys.map((key) => {
    const { year, month } = parseMonthKey(key);
    const sub = subRevenueByMonth.get(key);
    const cert = certRevenueByMonth.get(key);
    const refund = refundsByMonth.get(key);
    const churn = churnByMonth.get(key);

    const subRevenue = sub?.netRevenue ?? 0;
    const certRevenue = (cert?.fullPriceIncome ?? 0) + (cert?.premiumIncome ?? 0) + (cert?.otherDiscountedIncome ?? 0);
    const gross = subRevenue + certRevenue;
    const refunds = refund?.totalAmount ?? 0;

    return {
      year,
      month,
      subRevenue,
      certRevenue,
      gross,
      refunds,
      netRevenue: gross - refunds,
      churnRate: churn?.rate ?? null,
    };
  });

  return { rows, isLoading, isError };
};
